import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { aiApi } from '../../../api/ai.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

const usd = (n) => `$${Number(n || 0).toFixed(4)}`;
const num = (n) => Number(n || 0).toLocaleString('it-IT');

function passTone(rate) {
  if (rate >= 1) return 'success';
  if (rate >= 0.9) return 'warning';
  return 'danger';
}

export function JailbreakPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canManage = hasPermission('ai:configure');
  const [agentId, setAgentId] = useState('customer');
  const [estimate, setEstimate] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [openReportId, setOpenReportId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const agentsQuery = useQuery({
    queryKey: ['ai', 'agents'],
    queryFn: () => aiApi.getAgents()
  });
  const agentOptions = useMemo(() => {
    const list = (agentsQuery.data && agentsQuery.data.agents) || [];
    return list.map((a) => ({ value: a.id, label: a.label }));
  }, [agentsQuery.data]);

  const reportsQuery = useQuery({
    queryKey: ['ai', 'jailbreak', 'reports'],
    queryFn: () => aiApi.jailbreakReports({ limit: 20 })
  });

  const estimateMutation = useMutation({
    mutationFn: () => aiApi.jailbreakEstimate(agentId),
    onSuccess: (data) => { setEstimate(data); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const runMutation = useMutation({
    mutationFn: () => run(() => aiApi.jailbreakRun(agentId)),
    onSuccess: (data) => {
      setReport(data);
      setOpenReportId(String(data._id));
      setConfirmOpen(false);
      toast.success('Valutazione completata.');
      queryClient.invalidateQueries({ queryKey: ['ai', 'jailbreak', 'reports'] });
    },
    onError: (err) => {
      setConfirmOpen(false);
      toast.error(err.message || t('common.error'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => aiApi.jailbreakReportDelete(id),
    onSuccess: (_data, id) => {
      if (String(id) === String(openReportId)) { setReport(null); setOpenReportId(null); }
      setDeleteTarget(null);
      toast.success('Report eliminato.');
      queryClient.invalidateQueries({ queryKey: ['ai', 'jailbreak', 'reports'] });
    },
    onError: (err) => { setDeleteTarget(null); toast.error(err.message || t('common.error')); }
  });

  const detailQuery = useQuery({
    queryKey: ['ai', 'jailbreak', 'report', openReportId],
    queryFn: () => aiApi.jailbreakReport(openReportId),
    enabled: Boolean(openReportId)
  });
  const shown = detailQuery.data || report;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Test anti-jailbreak"
        description="Lancia una batteria di attacchi di prompt injection contro un agente e misura quanto resiste. La stima del costo non consuma token; il run sì."
      />

      <Card className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Select
              label="Agente da testare"
              options={agentOptions}
              value={agentId}
              disabled={!canManage || agentsQuery.isLoading}
              onChange={(e) => { setAgentId(e.target.value); setEstimate(null); }}
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => estimateMutation.mutate()}
            loading={estimateMutation.isPending}
            disabled={!canManage}
          >
            Stima costo
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canManage || !estimate}
            title={!estimate ? 'Esegui prima la stima' : undefined}
          >
            Esegui valutazione
          </Button>
        </div>

        {estimate && (
          <div className="rounded-lg border border-border bg-surface p-4 text-sm">
            <p className="mb-2 font-medium text-ink">
              Stima per {estimate.target.agentLabel} · modello <code className="text-xs">{estimate.target.model}</code>
            </p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-ink-muted sm:grid-cols-4">
              <span>Casi: <b className="text-ink">{estimate.cases}</b></span>
              <span>Token input: <b className="text-ink">~{num(estimate.estTokensIn)}</b></span>
              <span>Token output: <b className="text-ink">~{num(estimate.estTokensOutTypical)}–{num(estimate.estTokensOutMax)}</b></span>
              <span>Token totali: <b className="text-ink">~{num(estimate.estTokensTotalMax)}</b></span>
              <span>Prezzo in/out (/1M): <b className="text-ink">{usd(estimate.pricing.inputPerMillion)}/{usd(estimate.pricing.outputPerMillion)}</b></span>
              <span className="sm:col-span-3">
                Costo stimato: <b className="text-ink">{usd(estimate.estCostUsdTypical)} – {usd(estimate.estCostUsdMax)}</b>
                <span className="ml-1 text-xs">(prezzi: {estimate.pricing.source})</span>
              </span>
            </div>
          </div>
        )}
      </Card>

      {shown && (
        <Card className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-ink">
              Esito · {shown.target?.agentLabel} · <code className="text-xs">{shown.target?.model}</code>
            </h2>
            <Badge tone={passTone(shown.totals?.passRate ?? 0)}>
              Resistenza {Math.round((shown.totals?.passRate ?? 0) * 100)}%
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-ink-muted sm:grid-cols-4">
            <span>Superati: <b className="text-emerald-600">{shown.totals?.passed}</b>/{shown.totals?.total}</span>
            <span>Falliti: <b className="text-danger">{shown.totals?.failed}</b></span>
            <span>Costo reale: <b className="text-ink">{usd(shown.usage?.costUsd)}</b></span>
            <span>Token: <b className="text-ink">{num(shown.usage?.tokensIn)} in / {num(shown.usage?.tokensOut)} out</b></span>
            <span>Durata: <b className="text-ink">{((shown.durationMs || 0) / 1000).toFixed(1)}s</b></span>
            <span className="sm:col-span-3">Eseguito: <b className="text-ink">{fmt.dateTime(shown.createdAt)}</b></span>
          </div>

          <div className="flex flex-wrap gap-2">
            {(shown.byCategory || []).map((c) => (
              <Badge key={c.category} tone={c.failed > 0 ? 'danger' : 'success'}>
                {c.category}: {c.passed}/{c.total}
              </Badge>
            ))}
          </div>

          {shown.cases && (
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-surface text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Caso</th>
                    <th className="px-3 py-2 text-left">Categoria</th>
                    <th className="px-3 py-2 text-left">Esito</th>
                    <th className="px-3 py-2 text-left">Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.cases.map((c) => (
                    <tr key={c.id} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium text-ink">{c.title}</div>
                        <div className="text-xs text-ink-faint">{c.id}</div>
                      </td>
                      <td className="px-3 py-2 text-ink-muted">{c.category}</td>
                      <td className="px-3 py-2">
                        {c.passed
                          ? <Badge tone="success">Resistito</Badge>
                          : <Badge tone="danger">{c.errored ? 'Errore' : 'Violato'}</Badge>}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {!c.passed && c.reason ? <div className="mb-1 text-danger">{c.reason}</div> : null}
                        {!c.passed && c.evidence ? (
                          <div className="mb-1 rounded bg-danger/10 px-2 py-1 font-mono text-[11px] text-danger">{c.evidence}</div>
                        ) : null}
                        <div className="text-ink-faint">{c.responseExcerpt}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-ink">Report precedenti</h2>
        {reportsQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (reportsQuery.data?.items || []).length === 0 ? (
          <p className="text-sm text-ink-muted">Nessuna valutazione eseguita.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Agente</th>
                  <th className="px-3 py-2 text-left">Modello</th>
                  <th className="px-3 py-2 text-right">Resistenza</th>
                  <th className="px-3 py-2 text-right">Costo</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {reportsQuery.data.items.map((r) => (
                  <tr key={r._id} className="border-t border-border">
                    <td className="px-3 py-2 text-ink-muted">{fmt.dateTime(r.createdAt)}</td>
                    <td className="px-3 py-2 text-ink">{r.target?.agentLabel}</td>
                    <td className="px-3 py-2"><code className="text-xs text-ink-muted">{r.target?.model}</code></td>
                    <td className="px-3 py-2 text-right">
                      <Badge tone={passTone(r.totals?.passRate ?? 0)}>{Math.round((r.totals?.passRate ?? 0) * 100)}%</Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-ink-muted">{usd(r.usage?.costUsd)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          className="text-xs text-brand hover:text-brand-hover"
                          onClick={() => { setReport(null); setOpenReportId(String(r._id)); }}
                        >
                          Apri
                        </button>
                        {canManage && (
                          <button
                            type="button"
                            className="text-xs text-danger hover:underline"
                            onClick={() => setDeleteTarget(r)}
                          >
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Avviare la valutazione?"
        description={estimate
          ? `Verranno inviati ${estimate.cases} attacchi a ${estimate.target.agentLabel} (${estimate.target.model}). Costo stimato ${usd(estimate.estCostUsdTypical)} - ${usd(estimate.estCostUsdMax)}. Servira una verifica MFA recente.`
          : 'Esegui prima la stima.'}
        confirmLabel="Avvia"
        loading={runMutation.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => runMutation.mutate()}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminare il report?"
        description={deleteTarget
          ? `Il report del ${fmt.dateTime(deleteTarget.createdAt)} (${deleteTarget.target?.agentLabel}) sara eliminato definitivamente.`
          : ''}
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
      />
      <MfaPrompt {...mfaProps} />
    </div>
  );
}
